// Confirmation modal for permanently deleting a workspace and everything in it.
// Three states: confirm (typed name), refusal (live subscription and/or child
// team agent workspaces, D-20 and D-21), and a failure alert. The refusals come
// from the client pre-check (props) or from the server's failed-precondition
// error (error.details.reasons); the server stays authoritative.

import React from 'react';
import { Modal, Button, Field, Input } from '../primitives';
import {
    joinChildNames,
    REASON_ACTIVE_SUBSCRIPTION,
    REASON_HAS_TEAM_AGENTS,
} from '../../../utils/workspaceDeleteBlockers';

const noop = () => {};

function DeleteWorkspaceModal({
    workspaceName,
    onClose,
    onDelete,
    childWorkspaces = [],
    hasLiveSubscription = false,
    onGoToBilling = noop,
    onOpenTeamMembers = noop,
}) {
    const [typed, setTyped] = React.useState('');
    const [pending, setPending] = React.useState(false);
    const [failed, setFailed] = React.useState(false);
    const [serverReasons, setServerReasons] = React.useState([]);
    const [serverChildren, setServerChildren] = React.useState(null);

    const reasons = React.useMemo(() => {
        const set = new Set(serverReasons);
        if (hasLiveSubscription) set.add(REASON_ACTIVE_SUBSCRIPTION);
        if (childWorkspaces.length > 0) set.add(REASON_HAS_TEAM_AGENTS);
        return [REASON_ACTIVE_SUBSCRIPTION, REASON_HAS_TEAM_AGENTS].filter((r) => set.has(r));
    }, [serverReasons, hasLiveSubscription, childWorkspaces]);

    const children = serverChildren && serverChildren.length > 0 ? serverChildren : childWorkspaces;
    const hasSub = reasons.includes(REASON_ACTIVE_SUBSCRIPTION);
    const hasKids = reasons.includes(REASON_HAS_TEAM_AGENTS);

    const subSentence = `${workspaceName} has an active subscription. Deleting a team doesn't cancel it or refund it, so cancel your plan first, then come back to delete the team.`;
    const kidsNoun = children.length === 1 ? 'team agent workspace' : 'team agent workspaces';
    const kidsSentence = `${workspaceName} still has ${children.length} ${kidsNoun}: ${joinChildNames(children.map((c) => c.name))}. Delete or graduate ${children.length === 1 ? 'it' : 'each one'} first. Nothing is deleted for you.`;

    if (hasSub && hasKids) {
        return (
            <Modal title="Two things to finish first" onClose={onClose} maxWidth={400} footer={<Button variant="secondary" onClick={onClose}>Close</Button>}>
                <ul className="space-y-3 text-sm text-[color:var(--text-muted)]">
                    <li>
                        {subSentence}{' '}
                        <Button variant="link" size="sm" onClick={onGoToBilling}>Go to billing</Button>
                    </li>
                    <li>
                        {kidsSentence}{' '}
                        <Button variant="link" size="sm" onClick={onOpenTeamMembers}>Open team members</Button>
                    </li>
                </ul>
            </Modal>
        );
    }

    if (hasSub) {
        return (
            <Modal
                title="Cancel your plan first"
                onClose={onClose}
                maxWidth={400}
                footer={(
                    <>
                        <Button variant="secondary" onClick={onClose}>Close</Button>
                        <Button variant="primary" onClick={onGoToBilling}>Go to billing</Button>
                    </>
                )}
            >
                <p className="text-sm text-[color:var(--text-muted)]">{subSentence}</p>
            </Modal>
        );
    }

    if (hasKids) {
        return (
            <Modal
                title="Remove its agent workspaces first"
                onClose={onClose}
                maxWidth={400}
                footer={(
                    <>
                        <Button variant="secondary" onClick={onClose}>Close</Button>
                        <Button variant="primary" onClick={onOpenTeamMembers}>Open team members</Button>
                    </>
                )}
            >
                <p className="text-sm text-[color:var(--text-muted)]">{kidsSentence}</p>
            </Modal>
        );
    }

    const matches = typed.trim().toLowerCase() === String(workspaceName || '').trim().toLowerCase();

    const handleDelete = async () => {
        if (!matches || pending) return;
        setPending(true);
        setFailed(false);
        try {
            await onDelete?.();
            onClose?.();
        } catch (error) {
            const details = error?.details;
            const refused = Array.isArray(details?.reasons) ? details.reasons : [];
            if (refused.length > 0) {
                setServerChildren(Array.isArray(details.childWorkspaces) ? details.childWorkspaces : null);
                setServerReasons(refused);
            } else {
                setFailed(true);
            }
        } finally {
            setPending(false);
        }
    };

    return (
        <Modal
            title={`Delete ${workspaceName}?`}
            onClose={onClose}
            maxWidth={400}
            footer={(
                <>
                    <Button variant="secondary" onClick={onClose}>Keep team</Button>
                    <Button variant="danger" onClick={handleDelete} disabled={!matches || pending}>
                        {pending ? 'Deleting…' : 'Delete team'}
                    </Button>
                </>
            )}
        >
            <p className="mb-4 text-sm text-[color:var(--text-muted)]">
                {`This permanently deletes everything in ${workspaceName}: transactions, contacts, emails, files, templates, and settings. It can't be undone, and we can't get it back for you.`}
            </p>
            <Field label="Type the team name to confirm">
                <Input value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" />
            </Field>
            {failed && (
                <p role="alert" className="mt-3 text-sm text-[color:var(--color-danger)]">
                    Couldn't delete the team. Nothing was removed. Try again.
                </p>
            )}
        </Modal>
    );
}

export default DeleteWorkspaceModal;
